import { create } from 'zustand';
import type { Message } from '../services/chat.service';

export interface AppState {
  // Chat state
  messages: Message[];
  isLoadingChat: boolean;
  chatError: string | null;

  // Avatar state
  isAvatarSpeaking: boolean;
  isAvatarLoading: boolean;
  currentVisemes: any[];
  audioElement: HTMLAudioElement | null;

  // Audio state
  isRecording: boolean;
  audioLevel: number;

  // UI state
  showBookingForm: boolean;
  viewMode: 'chat' | 'form' | 'initial'; // initial = mostra entrambi i pulsanti
  lastSpokenFormStep: number; // Track last spoken form step to prevent duplicates
  instructionsSpoken: boolean; // Track if initial mode instructions have been spoken
  isChangingLanguage: boolean; // Track if language change is in progress to prevent audio overlap

  // Actions
  addMessage: (message: Message) => void;
  clearMessages: () => void;
  setLoadingChat: (loading: boolean) => void;
  setChatError: (error: string | null) => void;
  setAvatarSpeaking: (speaking: boolean) => void;
  setAvatarLoading: (loading: boolean) => void;
  setCurrentVisemes: (visemes: any[]) => void;
  setAudioElement: (audio: HTMLAudioElement | null) => void;
  setRecording: (recording: boolean) => void;
  setAudioLevel: (level: number) => void;
  showBookingFormModal: (show: boolean) => void;
  setViewMode: (mode: 'chat' | 'form' | 'initial') => void;
  setLastSpokenFormStep: (step: number) => void;
  setInstructionsSpoken: (spoken: boolean) => void;
  setIsChangingLanguage: (changing: boolean) => void;
}

export const useAppStore = create<AppState>((set) => ({
  // Initial state
  messages: [],
  isLoadingChat: false,
  chatError: null,
  isAvatarSpeaking: false,
  isAvatarLoading: false,
  currentVisemes: [],
  audioElement: null,
  isRecording: false,
  audioLevel: 0,
  showBookingForm: false,
  viewMode: 'initial',
  lastSpokenFormStep: -1,
  instructionsSpoken: false,
  isChangingLanguage: false,

  // Actions
  addMessage: (message: Message) =>
    set((state) => ({
      messages: [...state.messages, message],
    })),

  clearMessages: () =>
    set({
      messages: [],
      chatError: null,
    }),

  setLoadingChat: (loading: boolean) =>
    set({ isLoadingChat: loading }),

  setChatError: (error: string | null) =>
    set({ chatError: error }),

  setAvatarSpeaking: (speaking: boolean) =>
    set({ isAvatarSpeaking: speaking }),

  setAvatarLoading: (loading: boolean) =>
    set({ isAvatarLoading: loading }),

  setCurrentVisemes: (visemes: any[]) =>
    set({ currentVisemes: visemes }),

  setAudioElement: (audio: HTMLAudioElement | null) =>
    set({ audioElement: audio }),

  setRecording: (recording: boolean) =>
    set({ isRecording: recording }),

  setAudioLevel: (level: number) =>
    set({ audioLevel: level }),

  showBookingFormModal: (show: boolean) =>
    set({ showBookingForm: show }),

  setViewMode: (mode: 'chat' | 'form' | 'initial') =>
    set({ viewMode: mode }),

  setLastSpokenFormStep: (step: number) =>
    set({ lastSpokenFormStep: step }),

  setInstructionsSpoken: (spoken: boolean) =>
    set({ instructionsSpoken: spoken }),

  setIsChangingLanguage: (changing: boolean) =>
    set({ isChangingLanguage: changing }),
}));
